import { twMerge } from '@shared/themes';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { ProductLaunchpadItem } from '..';

type ProductLaunchpadItemKey = 'casino' | 'lotto' | 'live_casino' | 'scratchcards' | 'sports';

type ProductLaunchpadItemsProps = ComponentProps<'div'> & {
  items: ProductLaunchpadItemKey[];
};

const { CASINO, LOTTO, LIVE_CASINO, SCRATCHCARDS, SPORTS } = ROUTES;

const productLaunchpadItems = new Map<ProductLaunchpadItemKey, { href: string; image: string; alt: string; 'data-track-product': string }>([
  ['casino', { href: CASINO.DISCOVERY, image: '/images/home/home-product-launchpad/casino_new_tile.png', alt: 'Slot games with JP won', 'data-track-product': 'Casino' }],
  ['lotto', { href: LOTTO.DISCOVERY, image: '/images/home/home-product-launchpad/lotto_tile.png', alt: 'Half completed bingo card', 'data-track-product': 'Lotto' }],
  [
    'live_casino',
    { href: LIVE_CASINO.DISCOVERY, image: '/images/home/home-product-launchpad/live_casino_tile.png', alt: 'Roulette wheen with chip flying', 'data-track-product': 'Live Casino' },
  ],
  [
    'scratchcards',
    {
      href: SCRATCHCARDS.DISCOVERY,
      image: '/images/home/home-product-launchpad/scratch_tile.png',
      alt: 'Coin is scratching off a scratchcard',
      'data-track-product': 'Scratchcards',
    },
  ],
  ['sports', { href: SPORTS.DISCOVERY, image: '/images/home/home-product-launchpad/sport_tile.png', alt: 'Football pitch corner', 'data-track-product': 'Sports' }],
]);

const productLaunchpadItemGridClasses: Record<ProductLaunchpadItemKey, string> = {
  casino: 'col-span-2 md:col-span-3',
  sports: 'md:col-span-3',
  lotto: 'md:col-span-2',
  live_casino: 'md:col-span-2',
  scratchcards: 'md:col-span-2',
};

const productLaunchpadItemImageClasses: Partial<Record<ProductLaunchpadItemKey, string>> = {
  lotto: 'md:object-[0%_100%]',
};

const PRODUCT_LAUNCHPAD_FEATURED_ITEM_KEY: ProductLaunchpadItemKey = 'casino';

/**
 * ProductLaunchpadItems component is a component that displays a list of product launchpad items.
 * Each product launchpad item configuration is defined in the productLaunchpadItems map.
 * Component extends the div HTML element.
 *
 * @param {ProductLaunchpadItemsProps} props - The props for the ProductLaunchpadItems component.
 * @param {string[]} props.items - The items to display in the product launchpad.
 * @param {string} props.className - The class name to apply to the component.
 * @returns {JSX.Element} The ProductLaunchpadItems component.
 */
function ProductLaunchpadItems({ className, items, ...props }: ProductLaunchpadItemsProps) {
  const t = useTranslations('home.product_launchpad.products');

  if (items.length === 0) return null;

  const classes = twMerge('w-full max-w-164 grid grid-cols-[repeat(2,minmax(0,208px))] justify-around md:grid-cols-6 gap-2 md:gap-4 text-left', className);

  return (
    <div className={classes} {...props}>
      {items.map(item => {
        return (
          <ProductLaunchpadItem
            key={item}
            {...productLaunchpadItems.get(item)!}
            title={t(`${item}.title`)}
            description={t(`${item}.description`)}
            data-track-name="Card"
            className={productLaunchpadItemGridClasses[item]}
            imageClassName={productLaunchpadItemImageClasses[item]}
            featured={item === PRODUCT_LAUNCHPAD_FEATURED_ITEM_KEY}
          />
        );
      })}
    </div>
  );
}

export { ProductLaunchpadItems };
export type { ProductLaunchpadItemKey };
